import React from 'react'
import styled from 'styled-components'
import {
  Typography,
  Grid,
  InputAdornment,
  TextField,
  Button,
  Container,
  FormControlLabel,
  Checkbox,
  Paper,
  Snackbar,
  Box,
} from '@material-ui/core'

import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'

import Divider from '../components/Common/Divider'
import Fade from '../components/Common/Fade'
import Footer from '../components/Common/Footer'
import QS from '../utils/QS'
import {
  RegisterMember,
  Login,
  RequestResetPassword,
  RegisterBusiness,
} from '../utils/AppRequest'
import {
  WriteAccountToken,
  ValidAccountToken,
  ParseAccountToken,
} from '../utils/HandleToken'
import PathAndTitle from '../utils/PathAndTitle'
import { AppAuthHead } from '../components/Common/PagesHead'
import InlineMiddleSpan from '../components/Common/InlineMiddleSpan'
import NormalLink from '../components/Common/NormalLink'

const targetDefault = 'member'
const typeDefault = 'register'

type State = {
  target: 'business' | 'member'
  type: 'login' | 'register'
  email: string
  password: string
  password2: string
  loading: boolean
  agree: boolean
  openSnackBar: boolean
  snackBarMsg: string
}

export default class Auth extends React.PureComponent {
  state: State = {
    target: targetDefault,
    type: typeDefault,
    email: '',
    password: '',
    password2: '',
    loading: false,
    agree: false,
    openSnackBar: false,
    snackBarMsg: '',
  }

  handleChange = (type: string, value: any) => {
    this.setState({
      [`${type}`]: value,
    })
  }

  handleCloseSnackBar = () => {
    this.setState({
      openSnackBar: false,
      snackBarMsg: '',
    })
  }

  handleSnackBarOpen = (msg: string) => {
    this.setState({
      openSnackBar: true,
      snackBarMsg: msg,
    })
  }

  componentDidMount = () => {
    /**判斷登入模式 */
    const type = QS('type', window.location.href)
    const target = QS('target', window.location.href)
    if (type) this.handleChange('type', type)
    if (target) this.handleChange('target', target)
    /**自動登入 */
    ValidAccountToken(
      false,
      () => {
        const token: any = ParseAccountToken('biz')
        if (token && token.type) {
          if (token.type === 'business') {
            window.location.href = PathAndTitle.business.overview.path
          }
        }
      },
      () => {},
      'biz'
    )
  }

  handleEmailValidation = (email: string) => {
    let re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
  }

  handleActions = async () => {
    const { type, target, email, password, password2, agree } = this.state
    try {
      /**驗證 */
      if (email.length === 0 || password.length === 0) {
        throw '請輸入帳號與密碼'
      }
      if (password.length < 8) {
        throw '密碼格式為 8 個字元以上'
      }
      if (!this.handleEmailValidation(email)) {
        throw '帳號請輸入正確的信箱，請確認信箱格式是否正確'
      }
      if (type === 'register' && password !== password2) {
        throw '請確認確認密碼欄位中的密碼是否一致'
      }
      /**錯誤訊息 */
      const MsgUncorrect = '請確認帳號密碼輸入正確'
      const MsgSameEmail = `此信箱已註冊過(${email})`
      /**登入 */
      if (type === 'login') {
        this.handleChange('loading', true)
        const loginResult: any = await Login({
          email,
          password,
          type: target,
          oauth_origin: '',
        })
        this.handleChange('loading', false)
        if (loginResult && loginResult.errors === null) {
          /**寫入 token */
          WriteAccountToken(loginResult.payload.token)
          /**導向至帳戶首頁*/
          window.location.href =
            target === 'member'
              ? PathAndTitle.member.home.path
              : PathAndTitle.business.overview.path
        } else {
          throw MsgUncorrect
        }
      }
      if (type === 'register' && !agree) throw '請先同意我們的服務條款'
      /**商家註冊 */
      if (target === 'business' && type === 'register') {
        this.handleChange('loading', true)
        const RegisterBusinessResult: any = await RegisterBusiness({
          email,
          password,
          company_name: '',
          tax_id: '',
        })
        this.handleChange('loading', false)
        if (RegisterBusinessResult && RegisterBusinessResult.errors === null) {
          /**寫入 token */
          WriteAccountToken(RegisterBusinessResult.payload.token)
          /**導向至 onboarding 頁面  */
          window.location.href = `${PathAndTitle.business.overview.path}`
        } else {
          throw MsgSameEmail
        }
      }
      /**會員註冊 */
      if (target === 'member' && type === 'register') {
        this.handleChange('loading', true)
        const registerMemberResult: any = await RegisterMember({
          email,
          password,
          oauth_origin: '',
        })
        this.handleChange('loading', false)
        if (registerMemberResult && registerMemberResult.errors === null) {
          /**寫入 token */
          WriteAccountToken(registerMemberResult.payload.token)
          /**
           * 檢查是否為廠商邀請註冊會員
           * 返回廠商邀請頁面
           */
          const urlSearchParams = new URLSearchParams(window.location.search)
          const action = urlSearchParams.get('action')
          if (action === 'pi') {
            const from = urlSearchParams.get('from')
            window.location.href = `/business/partner-invite/${from}`
          } else {
            /**導向至會員 onboarding 頁面*/
            window.location.href = `${PathAndTitle.member.init.path}`
          }
        } else {
          throw MsgSameEmail
        }
      }
    } catch (err: any) {
      this.handleSnackBarOpen(err)
    }
  }

  // 重設密碼
  handleRequestResetPassword = async () => {
    try {
      if (this.state.email.length === 0)
        throw '請先於帳號欄位中輸入先前註冊的 Email。我們將寄發重置密碼的信件至您填寫的 Email'
      const requestResetPasswordResult: any = await RequestResetPassword({
        email: this.state.email,
        type: this.state.target,
      })
      if (
        requestResetPasswordResult &&
        requestResetPasswordResult.errors === null
      ) {
        this.handleSnackBarOpen(
          `重置密碼的信件已寄送至您的 Email ${this.state.email}`
        )
      }
    } catch (err: any) {
      this.handleSnackBarOpen(err)
    }
  }
  handleEnterLogin = async (event: any) => {
    if (event.keyCode === 13) {
      this.handleActions()
    }
  }

  render() {
    const targetText =
      this.state.target === 'business' ? '企業帳戶' : '個人帳戶'
    const typeText = this.state.type === 'login' ? '登入' : '註冊'
    return (
      <Fade>
        <>
          <AppAuthHead />
          <AuthWrap>
            <div className="authArea">
              <Paper className="authInner">
                <Container maxWidth="md">
                  <Divider />
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Grid
                        container
                        justify="space-between"
                        alignItems="center"
                      >
                        <Grid item>
                          <InlineMiddleSpan margin="0 3px 0 0">
                            <Typography variant="h4" color="primary">
                              {typeText}
                            </Typography>
                          </InlineMiddleSpan>
                          <InlineMiddleSpan>
                            <Typography variant="h4">{targetText}</Typography>
                          </InlineMiddleSpan>
                        </Grid>
                      </Grid>
                      <Divider />
                      <Box display="flex" justifyContent="space-around">
                        {this.state.target === 'member' ? (
                          <Box>
                            <img
                              style={{
                                width: '100%',
                              }}
                              className="authImg"
                              src={
                                'https://swap-img.swap.work/media/remotely.svg'
                              }
                            />
                          </Box>
                        ) : null}
                        {this.state.target === 'business' ? (
                          <Box>
                            <img
                              style={{
                                width: '100%',
                              }}
                              className="authImg"
                              src={
                                'https://swap-img.swap.work/media/business.svg'
                              }
                            />
                          </Box>
                        ) : null}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <div>
                        <Divider />
                        <TextField
                          label="帳號(電子信箱)"
                          type="email"
                          variant="outlined"
                          fullWidth
                          onChange={(e) =>
                            this.handleChange('email', e.target.value)
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailOutlinedIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Divider height={18} />
                        <TextField
                          label="密碼(8 個字元以上)"
                          type="password"
                          variant="outlined"
                          fullWidth
                          onKeyDown={(e) => this.handleEnterLogin(e)}
                          onChange={(e) =>
                            this.handleChange('password', e.target.value)
                          }
                          helperText={
                            this.state.type === 'login' ? (
                              <div
                                style={{
                                  margin: '-6px -15px 0 0',
                                  textAlign: 'right',
                                }}
                              >
                                <Button
                                  size="small"
                                  onClick={() =>
                                    this.handleRequestResetPassword()
                                  }
                                >
                                  忘記密碼
                                </Button>
                              </div>
                            ) : null
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                        {this.state.type === 'register' ? (
                          <div>
                            <Divider height={18} />
                            <TextField
                              label="再次輸入密碼"
                              type="password"
                              variant="outlined"
                              fullWidth
                              onChange={(e) =>
                                this.handleChange('password2', e.target.value)
                              }
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <LockOutlinedIcon />
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <Divider height={9} />
                            <div style={{ padding: '0 3px' }}>
                              <FormControlLabel
                                label={
                                  <Grid container alignItems="center">
                                    <Grid item>
                                      <small>完成註冊後，即表示您同意</small>
                                    </Grid>
                                    <Grid item>
                                      <NormalLink
                                        href={
                                          PathAndTitle.terms[this.state.target]
                                            .path
                                        }
                                        target="_blank"
                                      >
                                        <b style={{ cursor: 'pointer' }}>
                                          <small>《網站服務條款》</small>
                                        </b>
                                      </NormalLink>
                                    </Grid>
                                  </Grid>
                                }
                                control={
                                  <Checkbox
                                    color="primary"
                                    value={this.state.agree}
                                    onChange={(_, checked) =>
                                      this.handleChange('agree', checked)
                                    }
                                  />
                                }
                              />
                            </div>
                          </div>
                        ) : null}
                        <Divider />
                        <Button
                          variant="contained"
                          fullWidth
                          color="primary"
                          onSubmit={() => {
                            this.handleEnterLogin
                          }}
                          onClick={() => {
                            this.handleActions()
                          }}
                          disabled={this.state.loading}
                        >
                          {this.state.loading ? '請稍候...' : typeText}
                        </Button>

                        <Divider height={6} />
                        {/**切換登入或註冊 */}
                        <div style={{ textAlign: 'center' }}>
                          <Button
                            size="small"
                            onClick={() =>
                              this.handleChange(
                                'type',
                                this.state.type === 'register'
                                  ? 'login'
                                  : 'register'
                              )
                            }
                          >
                            {this.state.type === 'register'
                              ? '已經有帳號了？請點此『登入』'
                              : '尚未註冊會員，請點此『註冊』'}
                          </Button>
                        </div>
                        <Typography variant="subtitle2" align="right">
                          <small>
                            <Box
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                if (this.state.target === 'member') {
                                  window.location.href =
                                    '/auth?target=business&type=login'
                                } else {
                                  window.location.href =
                                    '/auth?target=member&type=login'
                                }
                              }}
                            >
                              {this.state.target === 'member'
                                ? '使用企業帳戶'
                                : '使用個人帳戶'}
                            </Box>
                          </small>
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>
                  <Divider />
                </Container>
              </Paper>
            </div>
            f
            <div className="footerArea">
              <Footer />
            </div>
            <Snackbar
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={this.state.openSnackBar}
              autoHideDuration={2000}
              onClose={this.handleCloseSnackBar}
              message={this.state.snackBarMsg}
            />
          </AuthWrap>
        </>
      </Fade>
    )
  }
}

const AuthWrap = styled.div`
  background: #f2f4f7;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  .authTypeChoolse {
    border: 1px solid #3f51b5;
    border-radius: 10px;
    box-shadow: 2px 2px black;
  }
  .authArea {
    padding: 3.6rem 0;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: top right;
    flex: 1 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    .authInner {
      max-width: 50vw;
      background: white;
      box-shadow: 1.5px 1.5px 3px 3px rgba(55, 55, 55, 0.1);
      box-sizing: border-box;
    }
    .authImg {
      /* width: 100%; */
      height: 180px;
      object-fit: contain;
      display: block;
    }
  }
  .footerArea {
    flex: 0 0 auto;
  }
  @media screen and (max-width: 900px) {
    .authArea {
      padding: 1.2rem 0;
      .authInner {
        margin: 0 18px;
        max-width: 100vw;
      }
    }
  }
`
